"use client";

import { useState, useEffect } from "react";
import { MapPin, X } from "lucide-react";
import { geoService, type Prediction } from "@/services/geoService";

interface AddressAutocompleteProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (value: string, details?: { street: string; postcode: string; city: string }) => void;
  placeholder?: string;
  className?: string;
  iconClassName?: string;
  required?: boolean;
}

function parseAddress(fullAddress: string) {
  const parts = fullAddress.split(",").map((p: string) => p.trim()).filter(Boolean);
  const countries = ["france", "belgique", "suisse", "monaco"];
  if (parts.length > 1 && countries.includes(parts[parts.length - 1].toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""))) {
    parts.pop();
  }
  const cpIdx = parts.findIndex((p: string) => /\b\d{5}\b/.test(p));
  if (cpIdx >= 0) {
    const m = parts[cpIdx].match(/\b(\d{5})\b/);
    if (m) {
      const postcode = m[1];
      const pos = parts[cpIdx].indexOf(postcode);
      const after = parts[cpIdx].substring(pos + postcode.length).trim();
      const before = parts[cpIdx].substring(0, pos).trim();
      const city = after || (cpIdx + 1 < parts.length ? parts[cpIdx + 1] : "");
      const left = parts.slice(0, cpIdx);
      if (before) left.push(before);
      return { street: left.filter(Boolean).join(", "), postcode, city };
    }
  }
  if (parts.length >= 2) {
    const city = parts.pop() || "";
    return { street: parts.join(", "), postcode: "", city };
  }
  return { street: parts.join(", "), postcode: "", city: "" };
}

export function AddressAutocomplete({
  id,
  name,
  value = "",
  onChange,
  placeholder = "Adresse de livraison",
  className = "",
  iconClassName = "",
  required = false,
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (isSelecting) {
        setIsSelecting(false);
        return;
      }

      if (query.length >= 3) {
        try {
          setLoading(true);
          const data = await geoService.getPredictions(query);
          const addresses = data.filter((d: Prediction) =>
            d.types.some((t) =>
              ["subpremise", "street_address", "geocode"].includes(t)
            )
          );
          setPredictions(addresses);
          setShowPredictions(true);
          setLoading(false);
        } catch (err) {
          console.error("Erreur API predictions:", err);
          setPredictions([]);
          setLoading(false);
        }
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [query, isSelecting]);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const handleSelectPlace = async (placeId: string, description: string) => {
    setIsSelecting(true);
    setPredictions([]);
    setShowPredictions(false);

    // Récupérer les détails complets (avec CP) uniquement pour l'adresse sélectionnée
    let fullAddress = description;
    try {
      const res = await geoService.getPlace(placeId);
      console.log("📍 getPlace [", placeId, "]:", JSON.stringify(res));
      const result = res.result?.[0];
      console.log("📍 label:", result?.label, "| components:", JSON.stringify(result?.adresse_components));
      const label = result?.label;
      if (label) fullAddress = label;
    } catch (err) {
      console.warn("⚠️ getPlace échoué pour", placeId, err);
    }

    setQuery(fullAddress);
    const { street, postcode, city } = parseAddress(fullAddress);

    onChange?.(fullAddress, {
      street: street || "",
      postcode: postcode || "",
      city: city || "",
    });
  };

  const handleClear = () => {
    setQuery("");
    setPredictions([]);
    setShowPredictions(false);
    onChange?.("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange?.(newValue);
  };

  return (
    <div className="relative w-full">
      <div className="relative">
        <MapPin className={`pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 ${iconClassName}`} />
        <input
          type="text"
          id={id}
          name={name}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowPredictions(true)}
          onBlur={() => {
            setTimeout(() => setShowPredictions(false), 300);
          }}
          className={className}
          required={required}
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {showPredictions && predictions.length > 0 && (
        <ul
          className="absolute z-50 mt-2 w-full rounded-lg border bg-white shadow-lg max-h-60 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {predictions.map((p) => (
            <li
              key={p.placeId}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPredictions(false);
                handleSelectPlace(p.placeId, p.description);
              }}
              role="button"
              tabIndex={0}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
            >
              {p.description}
            </li>
          ))}
        </ul>
      )}

      {loading && (
        <div className="absolute z-50 mt-2 w-full rounded-lg border bg-white shadow-lg p-2">
          <p className="text-sm text-gray-500">Recherche en cours...</p>
        </div>
      )}
    </div>
  );
}
